// frontend/src/components/cart/CartWithGuest.js - Complete Fixed Version with Persistence

import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProducts } from '../../actions/productActions';
import { removeItemFromCart, increaseCartItemQty, decreaseCartItemQty, getCartItemKey } from '../../slices/cartSlice';
import {
    Trash2,
    ShoppingBag,
    Minus,
    Plus,
    IndianRupee,
    AlertCircle,
    X,
    LogIn,
    ArrowRight,
    ChevronDown,
    ChevronUp,
    Eye,
    MapPin,
    Truck
} from 'lucide-react';
import MetaData from '../layouts/MetaData';
import { GuestCheckoutModal } from '../checkout/GuestCheckout';
import { CartItemWeight } from '../cart/WeightDisplay';
import axios from 'axios';

// Updated ProductCard component without discount support
const ProductCard = ({ product }) => {
    if (!product?.images?.[0]?.image) return null;
    
    const displayPrice = () => {
        if (product.priceType === 'perKg') {
            return (
                <span className="text-[#dc2626] font-bold text-sm">
                    ₹{product.price.toFixed(2)}/kg
                </span>
            );
        } else if (product.priceType === 'perDozen') {
            return (
                <span className="text-[#dc2626] font-bold text-sm">
                    ₹{product.dozenPrice.toFixed(2)}/dozen
                </span>
            );
        } else if (product.priceType === 'perPack') {
            return (
                <span className="text-[#dc2626] font-bold text-sm">
                    ₹{product.packPrice.toFixed(2)}/pack
                </span>
            );
        } else if (product.weightOptions && product.weightOptions.length > 0) {
            const visibleOptions = product.weightOptions.filter(opt => opt.visible);
            if (visibleOptions.length === 0) {
                return (
                    <div className="text-gray-500 text-xs">
                        Currently unavailable
                    </div>
                );
            }
            
            const prices = visibleOptions.map(opt => opt.price);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            
            return (
                <div className="text-[#dc2626] font-bold text-sm">
                    ₹{minPrice.toFixed(2)} - ₹{maxPrice.toFixed(2)}
                </div>
            );
        }
        
        return <div className="text-gray-500 text-sm">Price unavailable</div>;
    };
    
    return (
        <div className="transform transition-transform hover:-translate-y-1">
            <Link to={`/product/${product._id}`} className="block relative">
                <div className="relative h-32 sm:h-40 w-full">
                    <div className="w-full h-full overflow-hidden">
                        <img
                            src={product.images[0].image}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                            }}
                        />
                    </div>
                </div>
            </Link>
            
            <div className="pt-2 flex flex-col items-center text-center">
                <Link to={`/product/${product._id}`} className="block w-full">
                    <h3 className="font-medium text-xs text-gray-800 line-clamp-1 mb-1 hover:text-[#dc2626] transition-colors">
                        {product.name}
                    </h3>
                </Link>
                
                <div className="flex flex-col items-center mb-2">
                    {displayPrice()}
                </div>
                
                <Link
                    to={`/product/${product._id}`}
                    className="inline-block w-full text-center bg-[#dc2626] text-white py-1 rounded-md text-xs hover:bg-[#dc2626]/90 transition-colors mx-auto"
                >
                    View Details
                </Link>
            </div>
        </div>
    );
};

// Updated CartItem component without discount calculations
const CartItem = ({ item, removeItem, increaseQty, decreaseQty }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };
    
    const getItemPrice = () => {
        if (item.priceType === 'perDozen') {
            return item.dozenPrice || item.price;
        } else if (item.priceType === 'perPack') {
            return item.packPrice || item.price;
        }
        return item.price;
    };
    
    const itemPrice = getItemPrice();
    
    return (
        <div className="rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-4 md:p-6 bg-white">
            {/* Mobile View (Collapsed) */}
            <div className="flex sm:hidden items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to={`/product/${item.product}`} className="shrink-0">
                        <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                        />
                    </Link>
                    <div className="min-w-0 pr-2">
                        <Link
                            to={`/product/${item.product}`}
                            className="text-sm font-medium text-gray-800 hover:text-[#dc2626] transition-colors line-clamp-2"
                        >
                            {item.name}
                        </Link>
                        <div className="text-[#dc2626] font-medium text-sm mt-1">
                            ₹{itemPrice.toFixed(2)} × {item.quantity}
                        </div>
                    </div>
                </div>
                
                <button
                    onClick={toggleExpand}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600"
                >
                    <Eye className="w-4 h-4" />
                </button>
            </div>
            
            {/* Mobile View (Expanded) */}
            {isExpanded && (
                <div className="sm:hidden mt-3 pt-3 border-t border-gray-100">
                    <div className="flex flex-col gap-3">
                        <div>
                            <CartItemWeight item={item} />
                            <p className="text-sm text-gray-600">
                                Price: ₹{itemPrice.toFixed(2)}
                                {item.priceType === 'perKg' && '/kg'}
                                {item.priceType === 'perDozen' && '/dozen'}
                                {item.priceType === 'perPack' && '/pack'}
                            </p>
                        </div>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center border border-gray-200 rounded-lg">
                                <button
                                    onClick={() => decreaseQty(item)}
                                    disabled={item.quantity <= 1}
                                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors rounded-l-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Minus className="w-4 h-4" />
                                </button>
                                <input
                                    type="text"
                                    className="w-10 h-8 text-center border-x border-gray-200 font-medium text-sm"
                                    value={item.quantity}
                                    readOnly
                                />
                                <button
                                    onClick={() => increaseQty(item)}
                                    disabled={item.quantity >= 10}
                                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            
                            <button
                                onClick={() => removeItem(item)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#dc2626] hover:bg-red-50 transition-all"
                                title="Remove item"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Desktop View */}
            <div className="hidden sm:flex sm:items-center gap-6">
                <Link to={`/product/${item.product}`} className="shrink-0">
                    <img
                        src={item.image}
                        alt={item.name}
                        className="w-24 h-24 md:w-28 md:h-28 object-cover rounded-lg hover:opacity-90 transition-opacity"
                    />
                </Link>
                
                <div className="flex-1 min-w-0">
                    <Link
                        to={`/product/${item.product}`}
                        className="text-base font-semibold text-gray-800 hover:text-[#dc2626] transition-colors line-clamp-2"
                    >
                        {item.name}
                    </Link>
                    
                    <div className="mt-2">
                        <CartItemWeight item={item} />
                        <span className="text-[#dc2626] font-bold text-sm">
                            ₹{itemPrice.toFixed(2)}
                            {item.priceType === 'perKg' && '/kg'}
                            {item.priceType === 'perDozen' && '/dozen'}
                            {item.priceType === 'perPack' && '/pack'}
                        </span>
                    </div>
                </div>
                
                <div className="flex items-center gap-4">
                    <div className="flex items-center border border-gray-200 rounded-lg">
                        <button
                            onClick={() => decreaseQty(item)}
                            disabled={item.quantity <= 1}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors rounded-l-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Minus className="w-4 h-4" />
                        </button>
                        <input
                            type="text"
                            className="w-12 h-8 text-center border-x border-gray-200 font-medium text-sm"
                            value={item.quantity}
                            readOnly
                        />
                        <button
                            onClick={() => increaseQty(item)}
                            disabled={item.quantity >= 10}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                    
                    <button
                       onClick={() => removeItem(item)}
                       className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#dc2626] hover:bg-red-50 transition-all"
                       title="Remove item"
                   >
                       <Trash2 className="w-4 h-4" />
                   </button>
               </div>
           </div>
       </div>
   );
};

// FIXED Pincode Check Component with Persistence and Collapsible Section
const PincodeCheck = ({ onPincodeValidated, itemsTotal }) => {
    const [pincode, setPincode] = useState('');
    const [loading, setLoading] = useState(false);
    const [deliveryInfo, setDeliveryInfo] = useState(null);
    const [error, setError] = useState('');
    const [showDeliveryAreas, setShowDeliveryAreas] = useState(false);
    
    // FIXED: Load saved pincode data on component mount
    useEffect(() => {
        const savedDeliveryInfo = localStorage.getItem('selectedDeliveryInfo');
        const savedPincode = localStorage.getItem('selectedPincode');
        
        if (savedDeliveryInfo && savedPincode) {
            try {
                const parsedInfo = JSON.parse(savedDeliveryInfo);
                setPincode(savedPincode);
                setDeliveryInfo(parsedInfo);
                onPincodeValidated(true, parsedInfo);
            } catch (error) {
                console.error('Error parsing saved delivery info:', error);
                localStorage.removeItem('selectedDeliveryInfo');
                localStorage.removeItem('selectedPincode');
            }
        }
    }, [onPincodeValidated]);
    
    const checkPincode = async () => {
        if (!pincode || pincode.length !== 6) {
            setError('Please enter a valid 6-digit pincode');
            return;
        }
        
        try {
            setLoading(true);
            setError('');
            
            // API call to check pincode
            const { data } = await axios.post('/api/v1/pincode/check', {
                pincode: pincode
            });
            
            if (data.success && data.isServiceable) {
                setDeliveryInfo(data.data);
                onPincodeValidated(true, data.data);
                
                // FIXED: Save to localStorage for persistence
                localStorage.setItem('selectedDeliveryInfo', JSON.stringify(data.data));
                localStorage.setItem('selectedPincode', pincode);
                
                toast.success(data.message);
            } else {
                setError(data.message);
                setDeliveryInfo(null);
                onPincodeValidated(false, null);
                
                // Clear localStorage on error
                localStorage.removeItem('selectedDeliveryInfo');
                localStorage.removeItem('selectedPincode');
            }
        } catch (error) {
            console.error('Pincode check error:', error);
            const errorMessage = error.response?.data?.message || 'Error checking pincode';
            setError(errorMessage);
            setDeliveryInfo(null);
            onPincodeValidated(false, null);
            
            // Clear localStorage on error
            localStorage.removeItem('selectedDeliveryInfo');
            localStorage.removeItem('selectedPincode');
        } finally {
            setLoading(false);
        }
    };
    
    // Handle pincode change - clear saved data when user changes pincode
    const handlePincodeChange = (value) => {
        setPincode(value);
        setError('');
        
        // If user changes pincode, clear saved delivery info
        if (deliveryInfo && value !== localStorage.getItem('selectedPincode')) {
            setDeliveryInfo(null);
            onPincodeValidated(false, null);
            localStorage.removeItem('selectedDeliveryInfo');
            localStorage.removeItem('selectedPincode');
        }
    };
    
    return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-[#dc2626]" />
                <h3 className="font-semibold text-gray-800">Check Delivery Charges</h3>
            </div>
            
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        maxLength="6"
                        value={pincode}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            handlePincodeChange(value);
                        }}
                        placeholder="Enter 6-digit pincode"
                        className="flex-grow px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                    />
                    <button
                        onClick={checkPincode}
                        disabled={loading || pincode.length !== 6}
                        className="px-4 py-2 bg-[#dc2626] text-white text-sm rounded-lg hover:bg-[#dc2626]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                Checking...
                            </>
                        ) : (
                            'Get It'
                        )}
                    </button>
                </div>
                
                {error && (
                    <div className="flex items-start gap-1 text-red-600">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p className="text-sm">{error}</p>
                    </div>
                )}
                
                {deliveryInfo && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-green-600 mt-0.5" />
                            <div>
                                <p className="font-medium text-sm text-green-800">
                                    Delivery Available for {deliveryInfo.area}
                                </p>
                                <p className="text-xs text-green-600">
                                    Hub: {deliveryInfo.hubPlace} | Delivery Charge: ₹{deliveryInfo.deliveryCharge}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* FIXED: Collapsible "We currently deliver to" section */}
                <div className="border-t pt-3">
                    <button
                        onClick={() => setShowDeliveryAreas(!showDeliveryAreas)}
                        className="flex items-center justify-between w-full text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        <span>We currently deliver to:</span>
                        {showDeliveryAreas ? (
                            <ChevronUp className="w-4 h-4" />
                        ) : (
                            <ChevronDown className="w-4 h-4" />
                        )}
                    </button>
                    
                    {showDeliveryAreas && (
                        <div className="mt-2 text-xs text-gray-600 space-y-1">
                            <p>• Rajarajeshwari Nagar (560098)</p>
                            <p>• Kengeri (560060)</p>
                            <p>• Viswaneedam (560060)</p>
                            <p>• Bangalore Viswavidalaya (560056)</p>
                            <p>• Seegehalli (562130)</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CartWithGuest = () => {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   
   const { items } = useSelector(state => state.cartState);
   const { isAuthenticated } = useSelector(state => state.authState);
   const { products, loading } = useSelector(state => state.productsState);
   
   const [showGuestModal, setShowGuestModal] = useState(false);
   const [isPincodeValidated, setIsPincodeValidated] = useState(false);
   const [deliveryInfo, setDeliveryInfo] = useState(null);
   
   // FIXED: Check for saved pincode data on component mount
   useEffect(() => {
       const savedDeliveryInfo = localStorage.getItem('selectedDeliveryInfo');
       if (savedDeliveryInfo) {
           try {
               const parsedInfo = JSON.parse(savedDeliveryInfo);
               setDeliveryInfo(parsedInfo);
               setIsPincodeValidated(true);
           } catch (error) {
               console.error('Error parsing saved delivery info:', error);
               localStorage.removeItem('selectedDeliveryInfo');
               localStorage.removeItem('selectedPincode');
           }
       }
   }, []);
   
   useEffect(() => {
       dispatch(getProducts());
   }, [dispatch]);
   
   // Calculate item price without discounts
   const calculateItemPrice = (item) => {
       if (item.priceType === 'perDozen') {
           return (item.dozenPrice || item.price);
       } else if (item.priceType === 'perPack') {
           return (item.packPrice || item.price);
       }
       return item.price;
   };
   
   // Calculate total price without discounts
   const calculateTotalPrice = (items) => {
       return items.reduce((total, item) => {
           const itemPrice = calculateItemPrice(item);
           return total + (itemPrice * item.quantity);
       }, 0);
   };
   
   const increaseQty = (item) => {
       const newQty = item.quantity + 1;
       if (newQty <= 10) {
           dispatch(increaseCartItemQty(getCartItemKey(item)));
       }
   };
   
   const decreaseQty = (item) => {
       const newQty = item.quantity - 1;
       if (newQty >= 1) {
           dispatch(decreaseCartItemQty(getCartItemKey(item)));
       }
   };
   
   const removeItem = (item) => {
       dispatch(removeItemFromCart(getCartItemKey(item)));
   };
   
   const handlePincodeValidation = (isValid, info) => {
       setIsPincodeValidated(isValid);
       setDeliveryInfo(info);
   };
   
   const handleCheckout = () => {
       if (!isPincodeValidated) {
           toast.error('Please check delivery charges for your pincode first');
           return;
       }
       
       if (isAuthenticated) {
           navigate('/shipping');
       } else {
           setShowGuestModal(true);
       }
   };
   
   const handleLogin = () => {
       navigate('/login?redirect=shipping');
   };
   
   const handleContinueAsGuest = () => {
       localStorage.setItem('isGuestCheckout', 'true');
       setShowGuestModal(false);
       navigate('/guest-checkout');
   };
   
   // Empty cart view
   if (items.length === 0) {
       return (
           <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] py-12 px-4" style={{
               backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
               backgroundSize: 'cover',
               backgroundPosition: 'center'
           }}>
               <MetaData title={'Your Cart'} />
               <div className="text-[#dc2626] mb-6">
                   <ShoppingBag size={60} className="md:h-20 md:w-20" />
               </div>
               <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 font-['Crimson_Text'] text-center">
                   Your Cart is Empty
               </h2>
               <p className="text-gray-600 mb-8 text-center">
                   Looks like you haven't added any items to your cart yet
               </p>
               <Link
                   to="/shop"
                   className="px-6 py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-all duration-300 transform hover:scale-105 font-medium flex items-center gap-2 text-sm"
               >
                   <ShoppingBag className="w-5 h-5" />
                   <span>Continue Shopping</span>
               </Link>
           </div>
       );
   }
   
   // Calculate totals
   const itemsTotal = calculateTotalPrice(items);
   
   return (
       <Fragment>
           <MetaData title={'Your Cart'} />
           <div className="container mx-auto px-4 py-6 md:py-8 pt-28 md:pt-12" style={{
               backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
               backgroundSize: 'cover',
               backgroundPosition: 'center'
           }}>
               <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
                   <h2 className="text-xl md:text-2xl font-bold text-gray-800 font-['Crimson_Text'] mb-3 md:mb-0">
                       Your Cart
                       <span className="text-[#dc2626] ml-2">
                           ({items.length} item{items.length > 1 ? 's' : ''})
                       </span>
                   </h2>
                   <Link
                      to="/shop"
                      className="px-4 py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-all duration-300 transform hover:scale-105 font-medium flex items-center justify-center gap-2 text-sm w-full md:w-auto"
                  >
                      <ShoppingBag className="w-5 h-5" />
                      <span>Add More</span>
                  </Link>
              </div>
             
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
                  <div className="lg:col-span-2 space-y-4 md:space-y-6">
                      {items.map(item => (
                          <CartItem
                              key={item.product}
                              item={item}
                              removeItem={removeItem}
                              increaseQty={increaseQty}
                              decreaseQty={decreaseQty}
                          />
                      ))}
                  </div>
                 
                  <div className="lg:col-span-1 mt-4 lg:mt-0">
                      <div className="rounded-xl shadow-md p-4 md:p-6 sticky top-24 bg-white">
                          <h3 className="text-lg md:text-xl font-bold mb-4 md:mb-6 pb-3 md:pb-4 border-b text-gray-800 font-['Crimson_Text']">
                              Order Summary
                          </h3>
                         
                          {/* Pincode Check Component */}
                          <PincodeCheck
                             onPincodeValidated={handlePincodeValidation}
                              itemsTotal={itemsTotal}
                          />
                         
                          {/* FIXED Order Summary */}
                          <div className="space-y-3 md:space-y-4">
                              <div className="flex justify-between text-gray-600 text-sm">
                                  <span>Total Items:</span>
                                  <span className="font-medium">
                                      {items.reduce((acc, item) => acc + Number(item.quantity), 0)} units
                                  </span>
                              </div>
                             
                              <div className="flex justify-between text-gray-600 text-sm">
                                  <span>Items Subtotal:</span>
                                  <span className="font-medium">₹{itemsTotal.toFixed(2)}</span>
                              </div>
                             
                              <div className="flex justify-between text-gray-600 text-sm">
                                  <span>Delivery Charge:</span>
                                  <span className="font-medium">
                                      {deliveryInfo ? (
                                          `₹${deliveryInfo.deliveryCharge.toFixed(2)}`
                                      ) : (
                                          <span className="text-orange-500">Check pincode above</span>
                                      )}
                                  </span>
                              </div>
                             
                              {deliveryInfo && (
                                  <div className="text-xs text-gray-500">
                                      Delivery to: {deliveryInfo.area} via {deliveryInfo.hubPlace}
                                  </div>
                              )}
                             
                              {/* Only show total if delivery info is available */}
                              {deliveryInfo && (
                                  <div className="border-t pt-3 md:pt-4">
                                      <div className="flex justify-between items-center">
                                          <span className="font-medium">Total Amount:</span>
                                          <div className="flex items-center text-lg md:text-xl font-bold text-[#dc2626]">
                                              <IndianRupee className="w-4 h-4 md:w-5 md:h-5" />
                                              {(itemsTotal + deliveryInfo.deliveryCharge).toFixed(2)}
                                          </div>
                                      </div>
                                  </div>
                              )}
                             
                              {/* If no delivery info, show message to check pincode first */}
                              {!deliveryInfo && (
                                  <div className="border-t pt-3 md:pt-4">
                                      <div className="text-center text-gray-500 text-sm">
                                          Please check delivery charges above to see total amount
                                      </div>
                                  </div>
                              )}
                             
                              <button
                                  onClick={handleCheckout}
                                  disabled={!isPincodeValidated}
                                  className={`w-full mt-4 md:mt-6 py-2 rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-all duration-300 ${
                                      isPincodeValidated
                                          ? 'bg-[#dc2626] text-white hover:bg-[#dc2626]/90 transform hover:scale-105'
                                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  }`}
                              >
                                  <ShoppingBag className="w-5 h-5" />
                                  {isPincodeValidated ? 'Proceed to Checkout' : 'Check Delivery First'}
                              </button>
                             
                              {!isPincodeValidated && (
                                  <p className="text-xs text-center text-gray-500 mt-2">
                                      Please check delivery charges above to proceed
                                  </p>
                              )}
                          </div>
                      </div>
                  </div>
              </div>
             
              {/* Related Products Section */}
              {items.length > 0 && products && products.length > 0 && (
                  <div className="mt-10 md:mt-16 border-t pt-6 md:pt-8">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 font-['Crimson_Text']">
                          Related Products
                      </h2>
                     
                      {/* Desktop View: Grid */}
                      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {products
                              .filter(product =>
                                  !items.find(item => item.product === product._id)
                              )
                              .slice(0, 4)
                              .map((product) => (
                                  <ProductCard key={product._id} product={product} />
                              ))}
                      </div>
                     
                      {/* Mobile View: Horizontal Scroll */}
                      <div className="md:hidden relative overflow-x-auto pb-4">
                          <div className="flex space-x-3 pl-1">
                              {products
                                  .filter(product =>
                                      !items.find(item => item.product === product._id)
                                  )
                                  .slice(0, 6)
                                  .map((product, index) => (
                                      <div key={`${product._id}-${index}`} className="flex-shrink-0 w-32 sm:w-40">
                                          <ProductCard product={product} />
                                      </div>
                                  ))}
                          </div>
                          <div className="text-center text-gray-500 text-xs mt-2">
                              Swipe for more →
                          </div>
                      </div>
                  </div>
              )}
          </div>
         
          {showGuestModal && (
              <GuestCheckoutModal
                  isOpen={showGuestModal}
                  onClose={() => setShowGuestModal(false)}
                  onLogin={handleLogin}
                  onContinueAsGuest={handleContinueAsGuest}
              />
          )}
      </Fragment>
   );
};

export default CartWithGuest;